import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Anchor, Divider } from "@mantine/core";

import {
  formatDateOnlyForDisplay,
  parseDateOnly,
} from "../../../utils/dateOnly";
import { formatLabCalendarDate } from "../../../utils/labTime";
import { CaseInfoRow } from "./CaseInfoRow";

interface CaseInfoWidgetProps {
  caseId?: string;
  caseNumber?: string;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    sex?: string;
    mrn?: string;
  };
  physicianName?: string;
  dateCollected?: string;
  dateReceived?: string;
  dateReported?: string;
  surgicalCenterName?: string;
}

function formatDateOnly(value?: string): string | null {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : null;
}

function formatSex(sex?: string): string | null {
  if (!sex) return null;
  if (sex === "M") return "Male";
  if (sex === "F") return "Female";
  return sex;
}

export function CaseInfoWidget({
  caseId,
  caseNumber,
  patient,
  physicianName,
  dateCollected,
  dateReceived,
  dateReported,
  surgicalCenterName,
}: CaseInfoWidgetProps) {
  const navigate = useNavigate();

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.firstName, patient?.lastName]
  );
  const dateOfBirthDisplay = useMemo(
    () => formatDateOnly(patient?.dateOfBirth),
    [patient?.dateOfBirth]
  );
  const dateCollectedDisplay = useMemo(
    () => formatDateOnly(dateCollected),
    [dateCollected]
  );
  const dateReceivedDisplay = useMemo(
    () => formatDateOnly(dateReceived),
    [dateReceived]
  );
  const dateReportedDisplay = useMemo(
    () => (dateReported ? formatLabCalendarDate(dateReported) : null),
    [dateReported]
  );
  const sexDisplay = useMemo(() => formatSex(patient?.sex), [patient?.sex]);
  const hasPatientDetails = useMemo(
    () =>
      Boolean(patientName || dateOfBirthDisplay || sexDisplay || patient?.mrn),
    [dateOfBirthDisplay, patient?.mrn, patientName, sexDisplay]
  );
  const hasDates = useMemo(
    () =>
      Boolean(
        dateCollectedDisplay || dateReceivedDisplay || dateReportedDisplay
      ),
    [dateCollectedDisplay, dateReceivedDisplay, dateReportedDisplay]
  );

  const handleCaseNavigation = useCallback(() => {
    if (caseId) navigate(`/cases/${caseId}`);
  }, [caseId, navigate]);

  return (
    <Stack gap={6}>
      <CaseInfoRow
        label="Case Number"
        value={
          caseNumber ? (
            <Anchor
              size="xs"
              fw={600}
              onClick={handleCaseNavigation}
              style={{ cursor: caseId ? "pointer" : "default" }}
            >
              {caseNumber}
            </Anchor>
          ) : undefined
        }
      />
      <CaseInfoRow label="Physician" value={physicianName} />
      <CaseInfoRow label="Facility" value={surgicalCenterName} />

      {hasPatientDetails && <Divider my={4} />}
      <CaseInfoRow label="Patient" value={patientName || undefined} />
      <CaseInfoRow label="Date of Birth" value={dateOfBirthDisplay} />
      <CaseInfoRow label="Sex" value={sexDisplay} />
      <CaseInfoRow label="MRN" value={patient?.mrn} />

      {hasDates && <Divider my={4} />}
      <CaseInfoRow label="Date Collected" value={dateCollectedDisplay} />
      <CaseInfoRow label="Date Received" value={dateReceivedDisplay} />
      <CaseInfoRow label="Date Reported" value={dateReportedDisplay} />
    </Stack>
  );
}
